import { DeleteOutlined } from '@ant-design/icons';
import {
  App, Button, Drawer, Form, Input, InputNumber, Popconfirm, Select, Space,
} from 'antd';
import { getCompanies } from 'api/companies';
import { fetchAllCustomers } from 'api/customers';
import { createDeal, deleteDeal, updateDeal } from 'api/pipelines';
import React, { useEffect, useMemo, useState } from 'react';
import { getData } from 'utils';
import { getHttpErrorMessage } from 'utils/http';

function customerLabel(c) {
  return c.name || c.email || c.phone || `Customer #${c.id}`;
}

function stageOptionLabel(stage) {
  return stage.name;
}

function DealFormDrawer({
  open, deal, defaultStageKey, stages, onClose, onSaved,
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const customerOptions = useMemo(() => {
    const byId = new Map();
    customers.forEach((c) => {
      byId.set(c.id, { value: c.id, label: customerLabel(c) });
    });
    if (deal?.customer?.id && !byId.has(deal.customer.id)) {
      byId.set(deal.customer.id, {
        value: deal.customer.id,
        label: deal.customer.name || deal.customer.email || `Customer #${deal.customer.id}`,
      });
    }
    return [...byId.values()];
  }, [customers, deal]);

  const stageOptions = useMemo(() => {
    const options = (stages ?? []).map((s) => ({
      value: s.stage_key,
      label: stageOptionLabel(s),
    }));
    const currentKey = deal?.stage_key;
    if (currentKey && !options.some((o) => o.value === currentKey)) {
      options.unshift({
        value: currentKey,
        label: `${currentKey} (unknown stage)`,
      });
    }
    return options;
  }, [stages, deal]);

  const companyOptions = useMemo(() => {
    const byId = new Map();
    companies.forEach((c) => {
      byId.set(c.id, { value: c.id, label: c.name });
    });
    if (deal?.company?.id && !byId.has(deal.company.id)) {
      byId.set(deal.company.id, { value: deal.company.id, label: deal.company.name });
    }
    return [...byId.values()];
  }, [companies, deal]);

  useEffect(() => {
    if (!open) return;
    fetchAllCustomers({ page: 1, page_size: 100 }).then((res) => {
      const data = getData(res);
      const list = data?.data?.results ?? data?.results ?? [];
      setCustomers(Array.isArray(list) ? list : []);
    }).catch(() => {});
    getCompanies({ page: 1, page_size: 100 }).then((res) => {
      const data = getData(res);
      const list = data?.data?.results ?? data?.results ?? [];
      setCompanies(Array.isArray(list) ? list : []);
    }).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (deal) {
      form.setFieldsValue({
        title: deal.title,
        description: deal.description,
        stage_key: deal.stage_key,
        customer_id: deal.customer?.id,
        company_id: deal.company?.id,
        value: deal.value != null ? Number(deal.value) : undefined,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ stage_key: defaultStageKey });
    }
  }, [open, deal, defaultStageKey, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload = {
        title: values.title,
        description: values.description || '',
        stage_key: values.stage_key,
        customer_id: values.customer_id,
        company_id: values.company_id ?? null,
        value: values.value ?? null,
      };
      if (deal) {
        await updateDeal(deal.id, payload);
        message.success('Deal updated');
      } else {
        await createDeal({ ...payload, stage_key: values.stage_key || defaultStageKey });
        message.success('Deal created');
      }
      onSaved();
      onClose();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(getHttpErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deal) return;
    setDeleting(true);
    try {
      await deleteDeal(deal.id);
      message.success('Deal deleted');
      onSaved();
      onClose();
    } catch (err) {
      message.error(getHttpErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Drawer
      title={deal ? 'Edit deal' : 'New deal'}
      open={open}
      onClose={onClose}
      width={480}
      destroyOnClose
      footer={(
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {deal && (
              <Popconfirm
                title="Delete this deal?"
                description="This cannot be undone. Stage history for this deal will be removed."
                okText="Delete"
                okButtonProps={{ danger: true }}
                onConfirm={handleDelete}
              >
                <Button type="text" danger icon={<DeleteOutlined />} loading={deleting} disabled={submitting}>
                  Delete deal
                </Button>
              </Popconfirm>
            )}
          </div>
          <Space>
            <Button onClick={onClose} disabled={submitting || deleting}>
              Cancel
            </Button>
            <Button type="primary" loading={submitting} disabled={deleting} onClick={handleSubmit}>
              {deal ? 'Save changes' : 'Create'}
            </Button>
          </Space>
        </div>
      )}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Title is required' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item
          name="stage_key"
          label="Stage"
          rules={[{ required: true, message: 'Stage is required' }]}
        >
          <Select options={stageOptions} />
        </Form.Item>
        <Form.Item
          name="customer_id"
          label="Customer"
          rules={[{ required: true, message: 'Customer is required' }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            options={customerOptions}
          />
        </Form.Item>
        <Form.Item name="company_id" label="Company">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            options={companyOptions}
          />
        </Form.Item>
        <Form.Item name="value" label="Value (USD)">
          <InputNumber min={0} precision={2} style={{ width: '100%' }} prefix="$" />
        </Form.Item>
      </Form>
    </Drawer>
  );
}

export default DealFormDrawer;
